import { getApiClient } from '@/api/client';
import type { UseNotificationType } from '@/hooks/use-notification';
import { SIMULATION_LIST_QUERY_KEY } from '@/pages/price-planning/simulation/simulation-queries';
import {
  calculateAverageVat,
  computeSimulationIndexes,
  groupDataByPriceList,
  mapSimulationDataToRowData,
} from '@/pages/price-planning/simulation/simulation-tool-page/utils/simulation-helpers';
import type { PricelistSettings } from '@/pages/price-planning/simulation/simulation-tool-page/utils/simulation-types';
import {
  buildFieldComparisons,
  buildKeyFigureRequestItem,
  buildMismatchRequestItems,
  generateSimulationNameFromFile,
} from '@/pages/price-planning/simulation/utils/upload-simulation-helpers';
import { normalizeApiError } from '@/utils/error-utils';
import type { QueryClient } from '@tanstack/react-query';

type FileResult = {
  fileName: string;
  status: 'done' | 'skipped' | 'error';
  message?: string;
  fieldCount?: number;
  mismatchCount?: number;
};

/**
 * Extracts the fields from a single S3-hosted workbook, creates a draft simulation to compare
 * against, records any mismatched fields, then deletes the draft simulation again. Calls the
 * API client directly (rather than the React Query mutation hooks) so this keeps running to
 * completion even if the modal that triggered it has since been closed/unmounted.
 */
const processFileName = async (
  fileName: string,
  prefix: string,
): Promise<FileResult> => {
  const apiClient = getApiClient();
  const extraction = await apiClient.extractSimulationExcelFieldsFromS3File({
    fileName,
  });

  if (
    !extraction.success ||
    extraction.priceUnitId == null ||
    extraction.validFrom == null
  ) {
    return {
      fileName,
      status: 'skipped',
      message:
        extraction.errorMessage ??
        'Could not extract the simulation fields (missing Price Unit or Valid From Date) from the file.',
    };
  }

  const { simulationId: newSimulationId } = await apiClient.simulationStart({
    startSimulationRequestBody: {
      priceUnitIds: [extraction.priceUnitId],
      planningDate: extraction.validFrom,
      versionName: generateSimulationNameFromFile(fileName),
    },
  });

  try {
    const [detailedSimulation, simulationDetails, pricelistSettings] =
      await Promise.all([
        apiClient.getDetailedSimulation({ simulationId: newSimulationId }),
        apiClient.simulationSimulationId({ simulationId: newSimulationId }),
        apiClient.getPriceSettings({ simulationId: newSimulationId }),
      ]);

    const priceUnit = simulationDetails.planningPriceUnits?.find(
      (unit) => unit.id === extraction.priceUnitId,
    );
    const priceListName = priceUnit?.name ?? String(extraction.priceUnitId);
    const year = extraction.year ?? extraction.validFrom.getFullYear();

    // Reuse the same index computation as the Simulation Tool page's Simulation Index section so
    // the recorded key figures match what's displayed there for this simulation's price unit.
    const rowDataForVat = mapSimulationDataToRowData(
      detailedSimulation.simulationData,
      new Map(),
    );
    const groupedData = groupDataByPriceList(rowDataForVat);
    const pricelistSettingsMap = new Map<string, PricelistSettings>(
      (pricelistSettings.priceSettings ?? []).map((setting) => {
        const priceUnitId = (setting.priceUnitId ?? 0).toString();
        return [
          priceUnitId,
          {
            ...setting,
            averageVat: calculateAverageVat(groupedData[priceUnitId] ?? []),
          },
        ];
      }),
    );

    const indexRows = computeSimulationIndexes(
      mapSimulationDataToRowData(
        detailedSimulation.simulationData,
        pricelistSettingsMap,
      ),
      pricelistSettingsMap,
      simulationDetails.planningPriceUnits ?? [],
    );
    // Per-price-unit portfolio LL RRP index, needed to derive each product's PPC corridor mark.
    const totalLlRrpIndex = new Map<string, number>(
      indexRows.map((row) => [row.priceList.id.toString(), row.rrpIndex]),
    );

    const comparisons = buildFieldComparisons(
      extraction,
      detailedSimulation,
      pricelistSettingsMap,
      priceUnit?.type,
      totalLlRrpIndex,
    );

    const mismatchItems = buildMismatchRequestItems(
      comparisons,
      extraction.priceUnitId,
      priceListName,
      year,
    );

    const indexRow = indexRows.find(
      (row) => row.priceList.id === extraction.priceUnitId,
    );
    const keyFigureItem = buildKeyFigureRequestItem(
      extraction.keyFigures,
      indexRow,
      extraction.priceUnitId,
      priceListName,
      year,
      priceUnit?.type,
    );
    const keyFigureItems = keyFigureItem ? [keyFigureItem] : [];

    if (mismatchItems.length > 0 || keyFigureItems.length > 0) {
      await apiClient.recordSimulationExcelMismatches({
        recordSimulationMismatchesRequestBody: {
          prefix,
          mismatches: mismatchItems,
          keyFigures: keyFigureItems,
        },
      });
    }

    return {
      fileName,
      status: 'done',
      fieldCount: comparisons.length,
      mismatchCount: mismatchItems.length,
    };
  } finally {
    // The simulation only exists to extract the persisted values for comparison — remove it
    // once mismatches (if any) have been recorded so it doesn't linger in the simulation list.
    await apiClient.simulationDelete({ requestBody: [newSimulationId] });
  }
};

/**
 * Fires off the full excel-import flow for every file returned for the given date prefix and
 * runs it to completion in the background: lists the files, processes each one (extract, create
 * a draft simulation, compare, record mismatches, delete the draft simulation), then surfaces a
 * single summary toast once every file has been processed. Intended to be called without
 * awaiting so the caller (e.g. a modal) can close immediately and let this continue independently.
 */
export const runSimulationFilesImport = async ({
  prefix,
  queryClient,
  showNotification,
}: {
  prefix: string;
  queryClient: QueryClient;
  showNotification: UseNotificationType;
}): Promise<void> => {
  try {
    const filesResponse = await getApiClient().listSimulationExcelFiles({
      prefix,
    });
    const fileNames = filesResponse.fileNames ?? [];

    if (fileNames.length === 0) {
      showNotification({
        type: 'info',
        title: 'Import complete',
        description: `No simulation files were found for ${prefix}.`,
      });
      return;
    }

    const results: FileResult[] = [];
    for (const fileName of fileNames) {
      try {
        results.push(await processFileName(fileName, prefix));
      } catch (e) {
        results.push({
          fileName,
          status: 'error',
          message: normalizeApiError(e).message,
        });
      }
    }

    queryClient.invalidateQueries({ queryKey: [SIMULATION_LIST_QUERY_KEY] });

    const errorCount = results.filter((r) => r.status === 'error').length;
    const skippedCount = results.filter((r) => r.status === 'skipped').length;
    const totalMismatches = results.reduce(
      (sum, r) => sum + (r.mismatchCount ?? 0),
      0,
    );

    showNotification({
      type: errorCount > 0 ? 'error' : skippedCount > 0 ? 'warning' : 'success',
      title: 'Import complete',
      description: `Processed ${results.length} file(s) for ${prefix}: ${totalMismatches} mismatch(es) recorded${
        skippedCount ? `, ${skippedCount} skipped` : ''
      }${errorCount ? `, ${errorCount} failed` : ''}.`,
    });
  } catch (e) {
    showNotification({
      type: 'error',
      title: 'Import failed',
      description: normalizeApiError(e).message,
    });
  }
};
